import { extract, isEmptyArray } from '../array';
import { raise } from '../error';
import type { Optional } from './object-types/optional';

export function isObject(obj: unknown): obj is Record<string, unknown> {
  return typeof obj === 'object';
}

export function isEmptyObject<Obj extends Record<string, unknown>>(object: Optional<Obj>): object is Obj {
  if (isNullOrUndefined(object)) {
    return true;
  }

  const objectKeys = keys(object);

  return isEmptyArray(objectKeys);
}

export function isNotEmptyObject<Obj extends Record<string, unknown>>(object: Optional<Obj>): object is Obj {
  return !isEmptyObject(object);
}

export function isDeepEmptyObject<Obj extends object>(obj: Optional<Obj>) {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      if (value === undefined) {
        continue;
      }

      if (typeof value === 'object') {
        if (!isDeepEmptyObject(value)) {
          return false;
        }
      } else {
        return false;
      }
    }
  }

  return true;
}

export function isKeyOf<Obj extends Record<(keyof any), unknown>>(object: Obj, key: any | keyof Obj): key is keyof Obj {
  return key in object;
}

export function keyExists<Obj extends Record<(keyof any), unknown>>(object: Obj, key: string | null | keyof Obj): key is keyof Obj {
  return isNotNullOrUndefined(key) && isKeyOf(object, key);
}

export function isNullOrUndefined<ItemType>(obj: Optional<ItemType>): obj is null | undefined {
  return obj === null || obj === undefined;
}

export function isNotNullOrUndefined<ItemType>(obj: Optional<ItemType>): obj is NonNullable<ItemType> {
  return !isNullOrUndefined(obj);
}

export function areSomeNullOrUndefined(...objects: unknown[]) {
  return objects.some(isNullOrUndefined);
}

export function areAllNullOrUndefined(...objects: unknown[]) {
  return objects.every(isNullOrUndefined);
}

export function extend<Item extends {}>(object: Item, extension: Partial<Item>): Item {
  return {
    ...object,
    ...extension,
  };
}

export function keys<Item extends {}>(object: Item): (keyof Item)[] {
  return Object.keys(object) as (keyof Item)[];
}

export function values<ValuesType>(object: Record<keyof any, ValuesType>): ValuesType[] {
  return Object.values(object) as ValuesType[];
}

export function extractValues<Item extends Object, Key extends keyof Item>(object: Record<keyof any, Item>, key: Key): Item[keyof Item][] {
  const objectValues = values(object);

  return extract(objectValues, key);
}

export function entries<Item extends {}>(object: Item): [keyof Item, Item[keyof Item]][] {
  return Object.entries(object) as [keyof Item, Item[keyof Item]][];
}

export function omit<Item extends {}, Key extends keyof Item = keyof Item>(object: Item, keyToOmit: Key): Omit<Item, Key> {
  const { [keyToOmit]: _, ...rest } = object;

  return rest;
}

export function getObjectLength<Item extends {}>(object: Item): number {
  const objectKeys = keys(object);

  return objectKeys.length;
}

export function getRequiredObjectProperty<Obj extends Record<string, unknown>, Key extends keyof Obj = string>(object: Obj, key: Key): NonNullable<Obj[Key]> {
  const value = object[key];

  return isNotNullOrUndefined(value)
    ? value
    : raise(`Property ${key.toString()} is not defined`);
}

export function getOptionalObjectProperty<Obj extends Record<string, unknown>, Key extends keyof Obj>(object: Obj, key: Key): Optional<Obj[Key]> {
  const value = object[key];

  return value;
}

export function getObjectPropertyOrDefault<Obj extends Record<string, unknown>, Key extends keyof Obj, DefaultType>(object: Obj, key: Key, defaultValue: DefaultType): NonNullable<Obj[Key]> | DefaultType {
  const value = object[key];

  return isNotNullOrUndefined(value)
    ? value
    : defaultValue;
}

export function validateNotNullOrUndefined<Type>(value: Optional<Type> | unknown, name?: string): asserts value is NonNullable<Type> {
  if (isNullOrUndefined(value)) {
    raise(`${name ?? 'value'} is null or undefined`);
  }
}

export function validateNotEmptyObject<Type>(object: Type | undefined, name?: string): asserts object is Type {
  object ?? raise(`${name ?? 'Object'} is not defined`);
}

export function validateEnum<Type extends {}>(value: unknown, enumObject: Type): asserts value is Type[keyof Type] {
  if (!values(enumObject).some(enumValue => enumValue === value)) {
    raise(`Value ${value} is not a valid enum value`);
  }
}

export function validateEnumArray<Type extends {}>(items: unknown[], enumObject: Type): asserts items is Type[keyof Type][] {
  items.forEach(value => validateEnum(value, enumObject));
}

export function getFlatObjectValues<Type>(object: Optional<Record<string, Type[]>>): Type[] {
  return isNotNullOrUndefined(object) ? values(object).flat() : [];
}
